/**
 * Pure referral-display logic — the JSX-free half so link building, credit
 * formatting, and the reward lines are unit-tested without a DOM. Every
 * NUMBER/STATUS comes from the `ReferralSummaryView` the api returns; this only
 * composes the shareable link, converts micros → a display credit count, and
 * interpolates the reward copy. No domain logic: it never decides qualification
 * or payout (CLAUDE.md: cards/modals are display-only, api is authority).
 */
import type { Translator } from '@4f/ui';
import type { ReferralSummaryView } from '../../api/types.ts';
import { REFERRAL_PARAM } from './referral-capture.ts';

/** 1 credit = 1e6 micros (billing.md § "credits are the unit"), mirrors `billing-model`. */
const MICROS_PER_CREDIT = 1_000_000;

/** Convert a micros amount to a whole-number credit count for display. */
export function microsToCredits(micros: number): number {
  return Math.round(micros / MICROS_PER_CREDIT);
}

/**
 * Build the shareable invite link from the referrer's code and the app's own
 * origin.
 *
 * The link MUST resolve on the deployed SPA. This client is a HASH router
 * (`router/hash.ts`) whose only route shape is `#/c/<girlfriendId>` — there is no
 * `/signup` PATH, so a path-style invite link 404s (or boots the default route
 * with the code silently discarded, which is how the referral flywheel died).
 * The link therefore targets the app root with the code as a QUERY parameter:
 * the root always resolves, `location.search` is what {@link
 * import('./referral-capture.ts').captureReferralCode} reads, and a query
 * survives the hash router writing `#/…` afterwards.
 */
export function buildReferralLink(origin: string, referralCode: string): string {
  const base = origin.replace(/\/+$/, '');
  return `${base}/?${REFERRAL_PARAM}=${encodeURIComponent(referralCode)}`;
}

/** The two reward lines the dashboard renders, already translated + interpolated. */
export interface ReferralRewardLines {
  readonly signupBonus: string;
  readonly spendBack: string;
}

/**
 * The reward copy shown on the dashboard, interpolated from the SERVER-authoritative
 * summary (signup-bonus credits/batch size + spend-back %). Kept JSX-free so the
 * exact translated output is unit-tested without a DOM — this guards the i18n keys
 * and placeholder names against drift and proves the config path renders, not
 * hardcoded defaults.
 */
export function referralRewardLines(
  t: Translator,
  summary: Pick<
    ReferralSummaryView,
    'signupBonusCredits' | 'signupBonusBatchSize' | 'revsharePercent'
  >,
): ReferralRewardLines {
  return {
    signupBonus: t('referral.rewards.signupBonus', {
      credits: summary.signupBonusCredits,
      batchSize: summary.signupBonusBatchSize,
    }),
    spendBack: t('referral.rewards.spendBack', { percent: summary.revsharePercent }),
  };
}
